import React, { useEffect, useRef } from "react";
import { Route, Routes } from "react-router-dom";
import { useUserContext } from "./Contexts/UserProvider";
import Dashboard from "./Features/Components/Dashboard/Dashboard";
import UserManagement from "./Features/Components/UserManagement/UserManagement";
import DetailForm from "./Features/Components/CompanyProfile/DetailForm";
import Partners from "./Features/Components/Partners/Partner";
import Layout from "./Layout";
import AccountDashboard from "./Features/Components/Dashboard/AccountDashboard";
import Toast from "./Features/Components/Modals/Toast";
import Products from "./Features/Components/Products/ProductCatalogue";
import Approvals from "./Features/Components/Approvals/Approvals";
import { ProductType } from "./Features/Components/Design/ProductType";
import BlockchainDetailsPage from "./Features/Components/Approvals/BlockchainDetailPage";
import ProductDesign from "./Features/Components/Design/ProductDesign";
import ProductCategory from "./Features/Components/Design/ProductCategory";
import ProductDetailPage from "./Features/Components/Products/ProductDetailPage";
import { usePermissionManagement } from "./Features/Services/PermissionManagement";
import { ROUTES } from "./constants/routes";

/** Shared across remounts so React re-renders only produce one GET (+ one OPTIONS). */
let permissionsRequestKey: string | null = null;
let permissionsRequest: Promise<any> | null = null;
let permissionsLoadedForTenant: string | null = null;

const App = () => {
  const { getAllPermissionUserCategory } = usePermissionManagement();
  const { setPermissions, user } = useUserContext();
  const selectedTenantCode = user?.tenant || localStorage.getItem("tenantCode");
  const getPermissionsRef = useRef(getAllPermissionUserCategory);
  getPermissionsRef.current = getAllPermissionUserCategory;

  useEffect(() => {
    if (!selectedTenantCode) return;
    if (permissionsLoadedForTenant === selectedTenantCode) return;

    let cancelled = false;

    if (permissionsRequestKey !== selectedTenantCode || !permissionsRequest) {
      permissionsRequestKey = selectedTenantCode;
      permissionsRequest = getPermissionsRef.current();
    }

    permissionsRequest
      .then((response) => {
        if (cancelled || !Array.isArray(response)) return;

        const extractedKeys = response.flatMap((category: any) =>
          category.permissions.map((permission: any) => permission.key)
        );
        console.log("Extracted Permission Keys: ", extractedKeys);
        permissionsLoadedForTenant = selectedTenantCode;
        setPermissions(extractedKeys);
      })
      .catch((error) => {
        if (cancelled) return;
        permissionsRequestKey = null;
        permissionsRequest = null;
        console.error("Error fetching permission categories: ", error);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedTenantCode, setPermissions]);

  return (
    <>
      <Toast />
      <div className="flex flex-col min-h-screen">
        <Routes>
          <Route
            path="/*"
            element={<Layout routeType="securePublicRoute" />}
          >
            <Route path={ROUTES.PUBLIC.HOME} element={<AccountDashboard />} />
            <Route path={ROUTES.PUBLIC.BLOCKCHAIN_EXPLORER} element={<BlockchainDetailsPage data={null} />} />
            <Route path={ROUTES.PUBLIC.SUBSCRIPTIONS} element={<> Subscription </>} />
            <Route path={ROUTES.PUBLIC.CONTACT_US} element={<> Contact Us </>} />
            <Route path={ROUTES.PUBLIC.SETTINGS} element={<> Settings </>} />
          </Route>

          <Route
            path=":id"
            element={<Layout routeType="secureTenantRoutes" />}
          >
            <Route path={ROUTES.TENANT.HOME} element={<AccountDashboard />} />
            <Route path={ROUTES.TENANT.DASHBOARD} element={<Dashboard />} />
            <Route path={ROUTES.TENANT.USER_MANAGEMENT} element={<UserManagement />} />
            <Route path={ROUTES.TENANT.COMPANIES} element={<Partners />} />
            <Route path={ROUTES.TENANT.PRODUCTS} element={<Products />} />
            <Route path={ROUTES.TENANT.PRODUCT_DETAIL} element={<ProductDetailPage />} />
            <Route path={ROUTES.TENANT.PRODUCTS_TENANT} element={<Products />} />
            <Route path={ROUTES.TENANT.PRODUCT_TYPES} element={<ProductType />} />
            <Route path={ROUTES.TENANT.PRODUCT_DESIGN} element={<ProductDesign />} />
            <Route path={ROUTES.TENANT.PRODUCT_CATEGORY} element={<ProductCategory />} />
            <Route path={ROUTES.TENANT.BLOCKCHAIN_EXPLORER} element={<BlockchainDetailsPage data={null} />} />
            <Route path={ROUTES.TENANT.APPROVALS} element={<Approvals />} />
            <Route path={ROUTES.TENANT.DETAIL_FORM} element={<DetailForm />} />
          </Route>
        </Routes>
      </div>
    </>
  );
};

export default App;
